import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Table, Card, Button, Space, Typography, Grid, Tooltip, message, Tag, Modal } from 'antd';
import { PlusOutlined, SyncOutlined, EyeOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  getCycleClasses,
  getCycleMatieres,
  getCycleScoringMode,
  getCycleSousMatieres,
  getCycles,
  getCycleScoringConfiguration,
  createCycle,
  updateCycle,
} from './cycle.reducer';
import { getEcole } from '../ecole/ecole.reducer';
import { ScoringMode, scoringModeLabels, ICycle } from 'app/shared/model/cycle.model';

const { Title } = Typography;
const { useBreakpoint } = Grid;

interface CycleConfigurationProgress {
  completedSteps: number;
  totalSteps: number;
  missingStep?: string;
}

interface CycleProps extends RouteComponentProps<{ ecoleid: string; anneeScolaireId?: string }> {
  onConfigureCycle?: (cycleId: string) => void;
}

const hasCycleId = (cycle: ICycle): cycle is ICycle & { id: string } => Boolean(cycle.id);

export const Cycle = (props: CycleProps) => {
  const dispatch = useAppDispatch();
  const cycleList = useAppSelector(state => state.cycle.entities);
  const loading = useAppSelector(state => state.cycle.loading);
  const updating = useAppSelector(state => state.cycle.updating);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<ICycle | null>(null);
  const [classCountsByCycleId, setClassCountsByCycleId] = useState<Record<string, number>>({});
  const [configurationProgressByCycleId, setConfigurationProgressByCycleId] = useState<Record<string, CycleConfigurationProgress>>({});
  const [configurationStatusLoading, setConfigurationStatusLoading] = useState(false);
  const screens = useBreakpoint();
  const fromConfig =
    new URLSearchParams(props.location.search || '').get('fromConfig') === '1' ||
    props.location.pathname.includes('configuration-pedagogie');
  const basePath = props.match.params.anneeScolaireId
    ? `/cycle/${props.match.params.ecoleid}/${props.match.params.anneeScolaireId}`
    : `/cycle/${props.match.params.ecoleid}`;
  const configSuffix = fromConfig ? '?fromConfig=1' : '';

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getCycles(props.match.params.ecoleid));
  }, []);

  useEffect(() => {
    const fetchConfigurationProgress = async () => {
      setConfigurationStatusLoading(true);
      try {
        const entries = await Promise.all(
          cycleList.filter(hasCycleId).map(async cycle => {
            const [classes, matieres, scoringMode, configuration] = await Promise.all([
              dispatch(getCycleClasses(cycle.id)).unwrap(),
              dispatch(getCycleMatieres(cycle.id)).unwrap(),
              dispatch(getCycleScoringMode(cycle.id)).unwrap(),
              dispatch(getCycleScoringConfiguration(cycle.id)).unwrap(),
            ]);

            const matieresWithSousMatieres = matieres.filter(matiere => matiere.avecSousMatieres && matiere.cycleMatiereId);
            const sousMatiereCounts = await Promise.all(
              matieresWithSousMatieres.map(async matiere => {
                const sousMatieres = await dispatch(
                  getCycleSousMatieres({ cycleMatiereId: matiere.cycleMatiereId, cycleId: cycle.id, matiereId: matiere.id })
                ).unwrap();
                return sousMatieres.filter(sousMatiere => sousMatiere.actif !== false).length;
              })
            );
            const coefficientsRequired = scoringMode.usesCoefficients;
            const coefficientsConfigured = coefficientsRequired
              ? await Promise.all(
                  matieres.map(async matiere => {
                    const source = configuration.matieres.find(row => row.cycleMatiereId === matiere.cycleMatiereId)?.coefficientSource;
                    if (source === 'SOUS_MATIERES') {
                      const children = (await dispatch(getCycleSousMatieres({ cycleMatiereId: matiere.cycleMatiereId })).unwrap()).filter(
                        row => row.actif !== false
                      );
                      return (
                        children.length > 0 &&
                        children.every(child =>
                          classes.every(classe =>
                            configuration.sousMatiereCoefficients.some(
                              row =>
                                row.sousMatiereId === child.id &&
                                row.classeId === classe.id &&
                                Number.isInteger(row.coefficient) &&
                                row.coefficient >= 1
                            )
                          )
                        )
                      );
                    }
                    const coefficients = configuration.coefficients.filter(row => row.matiereId === matiere.id);
                    return classes.every(classe =>
                      coefficients.some(
                        coefficient =>
                          coefficient.classeId === classe.id &&
                          Number.isFinite(Number(coefficient.coefficient)) &&
                          Number(coefficient.coefficient) > 0
                      )
                    );
                  })
                )
              : [];

            const steps = [
              { label: 'Classes', completed: classes.length > 0 },
              { label: 'Matières', completed: matieres.length > 0 },
              {
                label: 'Sous-matières',
                completed: matieres.length > 0 && sousMatiereCounts.every(count => count > 0),
              },
            ];

            if (coefficientsRequired) {
              steps.push({
                label: 'Coefficients',
                completed: classes.length > 0 && matieres.length > 0 && coefficientsConfigured.every(Boolean),
              });
            }

            const summaryCompleted = steps.every(step => step.completed);
            const totalSteps = coefficientsRequired ? 5 : 4;
            const completedSteps = steps.filter(step => step.completed).length + Number(summaryCompleted);

            return [
              cycle.id,
              {
                classCount: classes.length,
                progress: {
                  completedSteps,
                  totalSteps,
                  missingStep: steps.find(step => !step.completed)?.label,
                },
              },
            ] as [string, { classCount: number; progress: CycleConfigurationProgress }];
          })
        );
        setClassCountsByCycleId(
          entries.reduce<Record<string, number>>((acc, [cycleId, count]) => {
            acc[cycleId] = count.classCount;
            return acc;
          }, {})
        );
        setConfigurationProgressByCycleId(
          entries.reduce<Record<string, CycleConfigurationProgress>>((acc, [cycleId, count]) => {
            acc[cycleId] = count.progress;
            return acc;
          }, {})
        );
      } catch (error) {
        message.error('Impossible de charger les statuts de configuration des cycles.');
      } finally {
        setConfigurationStatusLoading(false);
      }
    };

    if (cycleList.length) {
      fetchConfigurationProgress();
    } else {
      setClassCountsByCycleId({});
      setConfigurationProgressByCycleId({});
    }
  }, [cycleList, dispatch]);

  const handleSyncList = () => {
    dispatch(getCycles(props.match.params.ecoleid));
  };

  const closeFormModal = () => {
    if (!updating) {
      setFormModalOpen(false);
      setSelectedCycle(null);
    }
  };

  const handleSaveCycle = async values => {
    const entity = {
      ...selectedCycle,
      ...values,
      ecoleId: props.match.params.ecoleid,
    };
    const action = selectedCycle?.id ? updateCycle : createCycle;
    const result = await dispatch(action(entity));
    if (action.fulfilled.match(result)) {
      setFormModalOpen(false);
      setSelectedCycle(null);
    }
  };

  const navigate = (pathname: string) => props.history.push(`${pathname}${configSuffix}`);

  const handleDeleteCycle = (cycleId: string) => {
    if (classCountsByCycleId[cycleId] > 0) {
      message.warning('Impossible de supprimer un cycle avec des classes liées.');
      return;
    }
    navigate(`${basePath}/${cycleId}/delete`);
  };
  const handleConfigureCycle = props.onConfigureCycle;

  const renderConfigurationStatus = (cycleId?: string) => {
    if (!cycleId) {
      return '-';
    }

    const progress = configurationProgressByCycleId[cycleId];

    if (configurationStatusLoading || !progress) {
      return <Tag>Chargement</Tag>;
    }

    if (progress.completedSteps === progress.totalSteps) {
      return (
        <Space direction="vertical" size={0}>
          <Tag color="success" style={{ marginRight: 0 }}>
            Configuré
          </Tag>
        </Space>
      );
    }

    if (progress.completedSteps > 0) {
      return (
        <Space direction="vertical" size={0}>
          <Tag color="warning" style={{ marginRight: 0 }}>
            À compléter
          </Tag>
        </Space>
      );
    }

    return (
      <Space direction="vertical" size={0}>
        <Tag color="default" style={{ marginRight: 0 }}>
          Non configuré
        </Tag>
      </Space>
    );
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
    },
    {
      title: 'Mode de calcul',
      dataIndex: 'scoringMode',
      key: 'scoringMode',
      render: (type?: ScoringMode) => (type ? scoringModeLabels[type] : '-'),
    },
    {
      title: 'Statut',
      key: 'configuration',
      width: screens.xs ? 130 : 160,
      render: (_, record) => renderConfigurationStatus(record.id),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: screens.xs ? 100 : 200,
      render: (_, record) => (
        <Space size="middle">
          {handleConfigureCycle && (
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => handleConfigureCycle(record.id)}
              size={screens.xs ? 'small' : 'middle'}
              title="Configurer"
            />
          )}
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${basePath}/${record.id}/matieres`)}
            size={screens.xs ? 'small' : 'middle'}
            title="Afficher les matières"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedCycle(record);
              setFormModalOpen(true);
            }}
            size={screens.xs ? 'small' : 'middle'}
          />
          <Tooltip title={classCountsByCycleId[record.id] > 0 ? 'Impossible de supprimer un cycle avec des classes liées' : 'Supprimer'}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCycle(record.id)}
              size={screens.xs ? 'small' : 'middle'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px 24px' }} bodyStyle={{ padding: screens.xs ? '0 8px 16px' : '0 24px 24px' }}>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '1.5rem' : '2rem' }}>
          <Translate contentKey="korischoolApp.cycle.home.title">Cycles</Translate>
        </Title>
        <Space>
          <Button onClick={handleSyncList} className="d-flex align-items-center" disabled={loading} size={screens.xs ? 'small' : 'middle'}>
            <SyncOutlined spin={loading} />
            <Translate contentKey="korischoolApp.cycle.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            className="d-flex align-items-center justify-content-between gap-3 p-1 rounded"
            onClick={() => {
              setSelectedCycle(null);
              setFormModalOpen(true);
            }}
            icon={<PlusOutlined style={{ fontSize: screens.xs ? '12px' : '14px' }} />}
            style={{
              backgroundColor: '#F28E19',
              color: '#fff',
              fontWeight: 'bold',
              padding: screens.xs ? '6px 8px' : '8px 16px',
              fontSize: screens.xs ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Translate contentKey="korischoolApp.cycle.home.createLabel">Create new Cycle</Translate>
          </Button>
        </Space>
      </div>

      <Table
        dataSource={cycleList}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={loading}
        locale={{
          emptyText: <Translate contentKey="korischoolApp.cycle.home.notFound">No Cycles found</Translate>,
        }}
        bordered
        size="middle"
        style={{ backgroundColor: 'white' }}
        scroll={{ x: screens.xs ? 'true' : 'false' }}
      />

      <Modal
        title={selectedCycle ? 'Modifier le cycle' : 'Créer un cycle'}
        visible={formModalOpen}
        onCancel={closeFormModal}
        footer={null}
        destroyOnClose
        maskClosable={!updating}
        closable={!updating}
      >
        <ValidatedForm key={selectedCycle?.id || 'create'} defaultValues={selectedCycle || {}} onSubmit={handleSaveCycle}>
          <ValidatedField label={translate('korischoolApp.cycle.nom')} id="cycle-form-nom" name="nom" data-cy="nom" type="text" />
          <ValidatedField
            label={translate('korischoolApp.cycle.scoringMode')}
            id="cycle-form-scoringMode"
            name="scoringMode"
            data-cy="scoringMode"
            type="select"
            validate={{ required: { value: true, message: translate('entity.validation.required') } }}
          >
            <option value="" key="0" />
            {Object.values(ScoringMode).map(type => (
              <option value={type} key={type}>
                {scoringModeLabels[type]}
              </option>
            ))}
          </ValidatedField>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeFormModal} disabled={updating}>
              Annuler
            </Button>
            <Button htmlType="submit" loading={updating} style={{ backgroundColor: '#F28E19', color: 'white' }}>
              Enregistrer
            </Button>
          </div>
        </ValidatedForm>
      </Modal>
    </Card>
  );
};

export default Cycle;
